import { batchStatusClass, batchStatusLabel, fmtDateTime, isLegacy } from "@/lib/provenanceDisplay";

const HEADERS = [
  ["command", "command"], ["file", "file"], ["imported_at", "imported"], ["captured_at", "captured"],
  ["by", "by"], ["status", "status"], ["row_count", "rows"], ["imported_rows", "imported"],
  ["skipped_rows", "skipped"], ["sha256", "sha-256"], ["notes", "notes"],
];

/**
 * Import History table (Iter 38). One row per import batch, newest first.
 * Full SHA-256 lives in the detail panel — the table shows a 12-char prefix.
 */
export default function ImportBatchTable({ items, selectedId, onSelect }) {
  if (!items || items.length === 0) {
    return (
      <div className="p-12 text-center text-sm text-[var(--ml-text-secondary)]" data-testid="import-history-empty">
        No import batches match these filters.
      </div>
    );
  }
  return (
    <table className="w-full text-sm" data-testid="import-history-table">
      <thead>
        <tr className="border-b border-[var(--ml-border)] bg-[var(--ml-surface)]">
          {HEADERS.map(([key, label]) => (
            <th key={key} className="px-3 py-2 text-left ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] whitespace-nowrap">
              {label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {items.map((b, i) => {
          const legacy = isLegacy(b);
          const selected = b.id === selectedId;
          return (
            <tr
              key={b.id}
              onClick={() => onSelect(b.id)}
              className={`border-b border-[var(--ml-border)] last:border-b-0 cursor-pointer hover:bg-[var(--ml-primary)]/[0.04] ${
                selected ? "bg-[var(--ml-primary)]/[0.07]" : i % 2 ? "bg-[var(--ml-surface)]/30" : ""
              }`}
              data-testid={`import-batch-row-${b.id}`}
            >
              <td className="px-3 py-2 ml-mono text-xs font-semibold whitespace-nowrap">
                {b.source_command}
                <div className="text-[10px] font-normal text-[var(--ml-text-secondary)]">{b.entity}</div>
              </td>
              <td className="px-3 py-2 text-xs max-w-[220px] truncate" title={b.original_filename || ""}>
                {b.original_filename || (legacy ? <span className="text-[var(--ml-text-secondary)]">Unknown</span> : "—")}
                {b.ingest_channel === "bulk-zip" && (
                  <span className="ml-1 ml-mono text-[9px] uppercase tracking-widest text-[var(--ml-text-secondary)]">zip</span>
                )}
              </td>
              <td className="px-3 py-2 ml-mono text-xs whitespace-nowrap">{fmtDateTime(b.imported_at)}</td>
              <td className="px-3 py-2 ml-mono text-xs whitespace-nowrap">{legacy ? "Unknown" : fmtDateTime(b.captured_at)}</td>
              <td className="px-3 py-2 text-xs max-w-[160px] truncate" title={b.imported_by || ""}>
                {b.imported_by || (legacy ? "Unknown" : "—")}
              </td>
              <td className="px-3 py-2 whitespace-nowrap">
                <span className={`ml-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${batchStatusClass(b.status)}`}>
                  {batchStatusLabel(b.status)}
                </span>
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-xs">{b.row_count?.toLocaleString?.() ?? b.row_count}</td>
              <td className="px-3 py-2 text-right tabular-nums text-xs">{b.imported_row_count?.toLocaleString?.() ?? b.imported_row_count}</td>
              <td className="px-3 py-2 text-right tabular-nums text-xs">{b.skipped_row_count?.toLocaleString?.() ?? b.skipped_row_count}</td>
              <td className="px-3 py-2 ml-mono text-xs whitespace-nowrap">
                {b.file_sha256_short ? `${b.file_sha256_short}…` : <span className="text-[var(--ml-text-secondary)]">Unknown</span>}
              </td>
              <td className="px-3 py-2 text-xs max-w-[260px] truncate text-[var(--ml-text-secondary)]" title={b.notes || b.error_message || ""}>
                {b.error_message ? <span className="text-[var(--ml-error)]">{b.error_message}</span> : b.notes || "—"}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
